/**
 * Bank logo fallback chain.
 *
 * Plaid's `institutions/get` returns a base64 logo for *most* banks but
 * occasionally drops one (notoriously for Chase, BofA tiers, smaller
 * regionals). When the Plaid logo is missing, we fall back to Google's
 * free favicon service (Clearbit's Logo API shut down in Dec 2025).
 * No API key needed, cached on Google's CDN. Manual accounts still get
 * the initials monogram (no domain → no logo possible).
 *
 * Maintain the map sparingly — only the top US banks Eli's customers
 * are realistically connecting. Returns null if we don't have a
 * confident domain match (caller renders the monogram in that case).
 */

const BANK_DOMAIN_MAP = {
  // Top 25 US retail banks + the most common fintech / business banks.
  // Keyed by lowercased substring match — order matters when overlapping.
  'chase':            'chase.com',
  'jpmorgan chase':   'chase.com',
  'jpmorgan':         'jpmorgan.com',
  'jp morgan':        'jpmorgan.com',
  'bank of america':  'bankofamerica.com',
  'bofa':             'bankofamerica.com',
  'wells fargo':      'wellsfargo.com',
  'wellsfargo':       'wellsfargo.com',
  'citi':             'citi.com',
  'citibank':         'citi.com',
  'capital one':      'capitalone.com',
  'capitalone':       'capitalone.com',
  'us bank':          'usbank.com',
  'u.s. bank':        'usbank.com',
  'usbank':           'usbank.com',
  'pnc':              'pnc.com',
  'truist':           'truist.com',
  'td bank':          'td.com',
  'discover':         'discover.com',
  'huntington':       'huntington.com',
  'fifth third':      '53.com',
  'keybank':          'key.com',
  'regions':          'regions.com',
  'm&t':              'mtb.com',
  'ally':             'ally.com',
  'navy federal':     'navyfederal.org',
  'usaa':             'usaa.com',
  'american express': 'americanexpress.com',
  'amex':             'americanexpress.com',
  'charles schwab':   'schwab.com',
  'schwab':           'schwab.com',
  'fidelity':         'fidelity.com',
  'vanguard':         'vanguard.com',
  'mercury':          'mercury.com',
  'brex':             'brex.com',
  'ramp':             'ramp.com',
  'novo':             'novo.com',
  'bluevine':         'bluevine.com',
  'silicon valley bank': 'svb.com',
  'svb':              'svb.com',
  'first republic':   'firstrepublic.com',
  'goldman':          'goldmansachs.com',
  'marcus':           'marcus.com',
  'sofi':             'sofi.com',
  'venmo':            'venmo.com',
  'paypal':           'paypal.com',
  'cash app':         'cash.app',
  'square':           'squareup.com',
  'stripe':           'stripe.com',
  // Common credit unions / regionals seen in CountBucks support tickets.
  'bbva':             'bbvausa.com',
  'first national':   'firstnational.com',
  'comerica':         'comerica.com',
  'citizens':         'citizensbank.com',
  'first horizon':    'firsthorizon.com',
  'frost':            'frostbank.com',
  'mountain america': 'macu.com',
  'mountainamerica':  'macu.com',
  'penfed':           'penfed.org',
  'pen fed':          'penfed.org',
  'redstone federal': 'redfcu.org',
  'alliant':          'alliantcreditunion.org',
  'state employees':  'ncsecu.org',
  'becu':             'becu.org',
  'suncoast':         'suncoastcreditunion.com',
};

export function bankLogoFallback(...candidates) {
  // Accept any number of candidate strings (institution_name, account_name,
  // legal_name, etc.) and return the first logo URL we can confidently
  // resolve. Falls back to null so the caller can render a monogram.
  for (const raw of candidates) {
    if (!raw || typeof raw !== 'string') continue;
    const lc = raw.toLowerCase().trim();
    if (!lc) continue;
    // Find the most specific match (longest substring hit first).
    let bestKey = '';
    for (const key of Object.keys(BANK_DOMAIN_MAP)) {
      if (lc.includes(key) && key.length > bestKey.length) {
        bestKey = key;
      }
    }
    if (bestKey) {
      return `https://www.google.com/s2/favicons?domain=${BANK_DOMAIN_MAP[bestKey]}&sz=64`;
    }
  }
  return null;
}

/** "5 minutes ago", "2 hours ago", "yesterday", "3 days ago", etc.
 *  Pure JS — no date-fns dep to keep this importable everywhere. */
export function formatRelativeTime(iso) {
  if (!iso) return '';
  let d;
  try { d = new Date(iso); } catch { return ''; }
  if (Number.isNaN(d.getTime())) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (seconds < 60)       return 'just now';
  if (seconds < 3600)     return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400)    return `${Math.floor(seconds / 3600)} hr ago`;
  if (seconds < 172800)   return 'yesterday';
  if (seconds < 2592000)  return `${Math.floor(seconds / 86400)} days ago`;
  if (seconds < 31536000) return `${Math.floor(seconds / 2592000)} months ago`;
  return `${Math.floor(seconds / 31536000)} years ago`;
}
